export interface MedisanteDevice {
  readonly id: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string;
  readonly version: string;
  readonly imei: string;
  readonly imsi: string;
  readonly iccid?: string;
  readonly manufacturedAt: string;
  readonly isPinned?: boolean;
  readonly isArchived?: boolean;
  readonly versionInfo: {
    readonly batteryVoltageMin?: number;
    readonly batteryVoltageLow?: number;
    readonly signalStrengthMin?: number;
    readonly signalStrengthLow?: number;
  };

  readonly state: string;
  readonly owner: string;
  readonly targetSystems: string[];
  readonly tags: string[];
  readonly timeZone?: string;
  readonly weightUnit?: WeightUnit;
  readonly minimumWeight?: number;
  readonly bloodGlucoseUnit?: string;
  readonly heartbeatTimerDuration?: number;
  readonly heartbeatRetryCount?: number;
  readonly heartbeatRetryCountTime?: number;
  readonly currentHeartbeatTimerDuration?: number;

  readonly token?: {
    readonly confirmed?: {
      readonly value: string;
      readonly issued: string;
    };
  };

  readonly batteryVoltage?: number;
  readonly batteryVoltageHealth?: string;
  readonly batteryVoltageAverageHealth?: string;
  readonly signalStrength?: number;
  readonly signalStrengthHealth?: string;
  readonly signalStrengthAverageHealth?: string;

  readonly updatedAt: string;
  readonly lastMeasurementAt?: string;
  readonly lastCommunicationAt?: string;
  readonly nextCommunicationAt?: string;

  readonly measurementCount?: number;
  readonly measurementTripCount?: number;
  readonly properties?: {
    readonly types?: DeviceType[];
    readonly model?: string[];
    readonly region?: string[];
    readonly status?: string[];
    readonly muted?: string[];
    readonly testdevice?: string[];
  };

  readonly gatewayID?: string;
  readonly mac?: string;
  readonly sensors?: MedisanteDevice[];
  readonly lastStatusCode: number;

  readonly batteryVoltageAverage?: number;
  readonly signalStrengthAverage?: number;

  readonly deactivated?: boolean;
  readonly inStock?: boolean;

  readonly allowUnlinkedGateway?: boolean;
}

export interface MedisanteMetric {
  readonly deviceID: string;
  readonly deviceManufacturer: string;
  readonly deviceModel: string;
  readonly timestamp: string;
  readonly data?: { readonly [k: string]: string };
}

export interface MedisanteMeasurement {
  readonly metadata: {
    readonly receivedTime: string;
  };
}

export interface MedisanteReport {
  readonly id: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly org_id: string;
  readonly count: number;
  readonly targetSystems: string[];
  readonly batteryVoltage?: number;
  readonly batteryVoltageHealth?: string;
  readonly signalStrength?: number;
  readonly signalStrengthHealth?: string;
  readonly nextCommunicationAt?: string;
}

export enum DeviceModels {
  BC800 = 'BC800',
  BP800 = 'BP800',
  D40G = 'D40G',
  BG800 = 'BG800',
  BT105 = 'BT105',
  BT005 = 'BT005',
  PM100 = 'PM100',
  GW9017 = 'GW9017',
  ISGI = 'SmartLink',
  IR42 = 'IR42',
  PO200 = 'PO200',
  P30 = 'P30',
  W550 = 'W550',
  BM96 = 'BM96',
  // same device, different model
  BS2001G = 'BS-2001-G1',
  PS300CRPM = 'PS300C-RPM',
  // same device, different model
  LS802GP = 'LS802-GP',
  BM300CRPM = 'BM300C-RPM',

  FORA6 = 'FORA6',
  IR20 = 'IR20',
  P80 = 'P80',
  MS6110 = 'MS6110',
  AOJ25A = 'AOJ-25A',
  PC60NW = 'PC-60NW',
  BM1000 = 'BM1000',
}

export enum DeviceType {
  BodyWeight = 'Weight',
  BodyComposition = 'Composition',
  Glucose = 'Glucose',
  BloodPressure = 'Blood Pressure',
  Pulse = 'Pulse',
  ECG = 'ECG',
  Gateway = 'Gateway',
  Temperature = 'Temperature',
  SpO2 = 'SpO2',
}

export enum WeightUnit {
  Pound = 'lb',
  Stone = 'st',
  Kilogram = 'kg',
}

export interface ChartDataPoint {
  readonly timestamp: number;
  readonly [k: string]: number;
}

export interface RowProps {
  label: string;
  value?: string | number;
  shouldHide?: boolean;
}

export enum SensorsSortBy {
  SERIAL_NUMBER = 'SERIAL_NUMBER',
  MODEL = 'MODEL',
  MEASUREMENTS = 'MEASUREMENTS',
  MAC = 'MAC',
}

export const StatusCodeInfo = {
  BC800: [
    { key: 200, value: 'Success' },
    { key: 400, value: 'Bad request' },
    { key: 403, value: 'Forbidden' },
    { key: 404, value: 'Device or account not found' },
    { key: 405, value: 'Action is not allowed' },
    { key: 500, value: 'Server error' },
  ],
  BG800: [
    { key: 200, value: 'Success' },
    { key: 1, value: 'Invalid token' },
    { key: 2, value: 'Server error' },
    { key: 3, value: 'Parameter error' },
  ],
  BP800: [
    { key: 2000, value: 'Success' },
    { key: 2002, value: 'Device or account not found' },
    { key: 2003, value: 'Measurement redundant' },
    { key: 2004, value: 'Unusual measurement date' },
    { key: 4001, value: 'Invalid data format' },
    { key: 4004, value: 'Invalid data type' },
    { key: 4005, value: 'Bad request on format' },
    { key: 4006, value: 'Required field is empty' },
    { key: 4007, value: 'Lack of required fields' },
    { key: 4008, value: 'Invalid body encryption' },
    { key: 4010, value: 'Unknown error' },
    { key: 4030, value: 'Invalid token' },
  ],
  'BS-2001-G1': [
    { key: 200, value: 'Success' },
    { key: 500, value: 'Server error' },
  ],
  'PS300C-RPM': [
    { key: 200, value: 'Success' },
    { key: 500, value: 'Server error' },
  ],
  BT005: [
    { key: 200, value: 'Success' },
    { key: 500, value: 'Server error' },
  ],
  BT105: [
    { key: 200, value: 'Success' },
    { key: 500, value: 'Server error' },
  ],
  D40G: [
    { key: 2000, value: 'Success' },
    { key: 2002, value: 'Device or account not found' },
    { key: 2003, value: 'Measurement redundant' },
    { key: 2004, value: 'Unusual measurement date' },
    { key: 4001, value: 'Invalid data format' },
    { key: 4004, value: 'Invalid data type' },
    { key: 4005, value: 'Bad request on format' },
    { key: 4006, value: 'Required field is empty' },
    { key: 4007, value: 'Lack of required fields' },
    { key: 4008, value: 'Invalid body encryption' },
    { key: 4010, value: 'Unknown error' },
    { key: 4030, value: 'Invalid token' },
  ],
  GTEL: [
    { key: 2000, value: 'Success' },
    { key: 2002, value: 'Device or account not found' },
    { key: 2003, value: 'Measurement redundant' },
    { key: 2004, value: 'Unusual measurement date' },
    { key: 4001, value: 'Invalid data format' },
    { key: 4004, value: 'Invalid data type' },
    { key: 4005, value: 'Bad request on format' },
    { key: 4006, value: 'Required field is empty' },
    { key: 4007, value: 'Lack of required fields' },
    { key: 4008, value: 'Invalid body encryption' },
    { key: 4010, value: 'Unknown error' },
    { key: 4030, value: 'Invalid token' },
  ],
  GW9017: [
    { key: 2000, value: 'Success' },
    { key: 2002, value: 'Device or account not found' },
    { key: 2003, value: 'Measurement redundant' },
    { key: 2004, value: 'Unusual measurement date' },
    { key: 4001, value: 'Invalid data format' },
    { key: 4004, value: 'Invalid data type' },
    { key: 4005, value: 'Bad request on format' },
    { key: 4006, value: 'Required field is empty' },
    { key: 4007, value: 'Lack of required fields' },
    { key: 4008, value: 'Invalid body encryption' },
    { key: 4010, value: 'Unknown error' },
  ],
  SmartLink: [
    { key: 2000, value: 'Success' },
    { key: 2002, value: 'Device or account not found' },
    { key: 2003, value: 'Measurement redundant' },
    { key: 2004, value: 'Unusual measurement date' },
    { key: 4001, value: 'Invalid data format' },
    { key: 4004, value: 'Invalid data type' },
    { key: 4005, value: 'Bad request on format' },
    { key: 4006, value: 'Required field is empty' },
    { key: 4007, value: 'Lack of required fields' },
    { key: 4008, value: 'Invalid body encryption' },
    { key: 4010, value: 'Unknown error' },
  ],
  'LS802-GP': [
    { key: 200, value: 'Success' },
    { key: 500, value: 'Server error' },
  ],
  'BM300C-RPM': [
    { key: 200, value: 'Success' },
    { key: 500, value: 'Server error' },
  ],
  PM100: [
    { key: 200, value: 'Success' },
    { key: 500, value: 'Server error' },
  ],
  BM1000: [
    { key: 200, value: 'Success' },
    { key: 500, value: 'Server error' },
  ],
};

export const isBluetoothDevice = (model: string) => {
  return (
    [
      'IR42',
      'P30',
      'PO200',
      'W550',
      'FORA6',
      'BM96',
      'IR20',
      'P80',
      'MS6110',
      'AOJ-25A',
    ].indexOf(model) > -1
  );
};

export const isGatewayDevice = (model: string) => {
  return ['GW9017', 'SmartLink'].indexOf(model) > -1;
};
